import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getALLblog } from "../services/blogService";

const POSTS_PER_PAGE = 6;

export default function BlogSidebarPage() {
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchLiveBlogs = async () => {
      try {
        setLoading(true);
        const res = await getALLblog();
        setBlogPosts(res.data.data || res.data || []);
      } catch (error) {
        console.error("Error fetching blogs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchLiveBlogs();
  }, []);

  const totalPages = Math.max(1, Math.ceil(blogPosts.length / POSTS_PER_PAGE));
  const paginatedPosts = blogPosts.slice(
    (currentPage - 1) * POSTS_PER_PAGE,
    currentPage * POSTS_PER_PAGE,
  );

  if (loading) {
    return (
      <div className="text-center py-20 font-medium text-gray-500">
        Loading blog page...
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen font-sans antialiased text-gray-600 selection:bg-[#43a047] selection:text-white">
      <div className="w-full">
        <div
          className="w-full h-32 bg-cover bg-center relative flex items-center p-8 border-b border-gray-100 overflow-hidden"
          style={{ backgroundImage: "url(/img/bg/banner18.jpg)" }}
        >
          <nav className="text-xs text-gray-400 font-medium tracking-wide justify-center items-center m-auto text-center">
            <Link to="/" className="hover:text-[#43a047] transition-colors">
              Home
            </Link>
            <span className="mx-2 text-gray-300">/</span>
            <span className="text-gray-600">Blog Sidebar</span>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="flex flex-col lg:flex-row gap-10 items-start">
          {/* Left Layout Sidebar */}
          <aside className="w-full lg:w-[240px] flex-shrink-0 space-y-10 order-1">
            <div className="space-y-3">
              <h4 className="text-[11px] font-bold text-gray-800 tracking-wider uppercase border-b border-gray-100 pb-2">
                Search
              </h4>
              <div className="space-y-2">
                <input
                  type="text"
                  placeholder="Search..."
                  className="w-full border border-gray-200 px-3 py-2 text-xs focus:outline-none focus:border-gray-400 text-gray-700 bg-white"
                />
                <button className="w-auto bg-[#222222] text-white text-[10px] font-bold uppercase tracking-widest px-5 py-2 hover:bg-black transition-colors rounded-xs">
                  Search
                </button>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="text-[11px] font-bold text-gray-800 tracking-wider uppercase border-b border-gray-100 pb-2">
                Recent Comments
              </h4>
              <div className="space-y-3.5 text-xs">
                {[
                  { author: "demo", topic: "Quisque semper nunc" },
                  { author: "admin", topic: "Quisque orci porta..." },
                  { author: "demo", topic: "Quisque semper nunc" },
                ].map((comment, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-3 border-b border-gray-50 pb-2.5 last:border-0 last:pb-0"
                  >
                    <div className="w-8 h-8 rounded-full bg-gray-500 text-white font-serif font-bold text-sm flex items-center justify-center flex-shrink-0">
                      R
                    </div>
                    <div className="text-[11px] leading-relaxed text-gray-400">
                      <span className="text-gray-500 hover:text-[#43a047] font-medium cursor-pointer">
                        {comment.author}
                      </span>{" "}
                      says:{" "}
                      <span className="text-gray-600 block italic">
                        {comment.topic}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="text-[11px] font-bold text-gray-800 tracking-wider uppercase border-b border-gray-100 pb-2">
                Recent Posts
              </h4>
              <div className="space-y-4">
                {blogPosts.slice(0, 3).map((p) => (
                  <div
                    key={p.blog_id}
                    className="flex items-center gap-3 group cursor-pointer"
                  >
                    <img
                      src={p.full_image_url || p.image}
                      alt=""
                      className="w-12 h-12 object-cover bg-gray-50 border border-gray-100 p-0.5 rounded-xs"
                    />
                    <div className="min-w-0">
                      <h5 className="text-[11px] font-medium text-gray-700 group-hover:text-[#43a047] truncate transition-colors">
                        <Link to={`/blog-details/${p.blog_id}`}>{p.title}</Link>
                      </h5>
                      <p className="text-[10px] text-gray-400 mt-0.5">
                        {new Date(p.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </aside>

          {/* Right Main Blog Presentation Grid */}
          <div className="flex-1 order-2 space-y-12 w-full">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10">
              {paginatedPosts.map((post) => (
                <article
                  key={post.blog_id}
                  className="bg-white flex flex-col space-y-3 text-left group"
                >
                  <div className="w-full aspect-square bg-gray-50 overflow-hidden rounded-xs border border-gray-100">
                    <Link to={`/blog-details/${post.blog_id}`}>
                      <img
                        src={post.full_image_url || post.image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-102 transition-transform duration-300"
                      />
                    </Link>
                  </div>

                  <div className="space-y-1.5 flex-1 flex flex-col justify-between">
                    <div>
                      <h3 className="text-xs font-semibold text-gray-800 leading-snug tracking-tight hover:text-[#43a047] transition-colors line-clamp-2 cursor-pointer">
                        <Link to={`/blog-details/${post.blog_id}`}>
                          {post.title}
                        </Link>
                      </h3>
                    </div>

                    <div className="text-[10px] text-gray-400 font-normal flex items-center gap-1 pt-1 border-t border-gray-50">
                      <span>
                        {new Date(post.created_at).toLocaleDateString()}
                      </span>
                      <span className="text-gray-300">|</span>
                      <span className="text-gray-400 hover:text-[#43a047] transition-colors font-medium">
                        {post.subject || "General"}
                      </span>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {/* Pagination — only renders if more than 1 page */}
            {totalPages > 1 && (
              <div className="border-t border-gray-100 pt-8 mt-4 flex justify-center">
                <div className="flex items-center gap-1 text-[11px] font-medium text-gray-600">
                  {/* Prev */}
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-2 h-6 flex items-center justify-center bg-gray-50 border border-gray-200 hover:border-gray-400 text-gray-400 rounded-xs transition-colors text-[10px] disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    &lt;&lt;
                  </button>

                  {/* Page number buttons */}
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`w-6 h-6 flex items-center justify-center rounded-xs transition-colors ${
                          currentPage === page
                            ? "bg-[#43a047] text-white"
                            : "bg-gray-50 border border-gray-200 hover:border-gray-400 text-gray-500"
                        }`}
                      >
                        {page}
                      </button>
                    ),
                  )}

                  {/* Next */}
                  <button
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="px-2 h-6 flex items-center justify-center bg-gray-50 border border-gray-200 hover:border-gray-400 text-gray-400 rounded-xs transition-colors text-[10px] disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    &gt;&gt;
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
